import { useCallback, useEffect, useMemo } from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import useLoggedNavigate from 'hooks/useLoggedNavigate'
import { Button, Dialog, DialogActions } from '@mui/material'
import {
  BottomContent,
  ButtonContainer,
  DialogContentCenterbody,
  Title,
  UnauthorizedDialog,
} from './InactiveUser.styled'
import InvertColorsOffIcon from '@mui/icons-material/InvertColorsOff'
import useAccessPermissions from 'hooks/useAccessToken/useAccessPermissions'
import { XAPIPermissions } from 'types/custom.d.ts'
import { useProfile } from 'Contexts/ProfileContext'

function InactiveUser() {
  const auth0 = useAuth0()
  const { isAuthenticated, isLoading, logout } = auth0
  const permissions = useAccessPermissions()
  const profileContext = useProfile()
  const nav = useLoggedNavigate()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      nav('/')
      return
    }
  })

  const impersonating = useMemo(
    () =>
      permissions.loaded &&
      permissions.permissions.has(XAPIPermissions.ImpersonateDatasightUser),
    [permissions.loaded, permissions.permissions]
  )

  const impersonate = useCallback(() => {
    if (
      permissions.loaded &&
      permissions.permissions.has(XAPIPermissions.ImpersonateDatasightUser)
    ) {
      profileContext.utilities.clearImposterProfileID()
      nav('/impersonate')
      return
    }
  }, [permissions.loaded, permissions.permissions])

  const tryAgain = useCallback(() => {
    if (
      permissions.loaded &&
      permissions.permissions.has(XAPIPermissions.ImpersonateDatasightUser)
    ) {
      const profileID =
        profileContext.dependentData.userDetails.imposterProfileID
      window.location.href = `${window.location.origin}/impersonate?ProfileID=${profileID}`
      return
    }
  }, [
    permissions.loaded,
    permissions.permissions,
    profileContext.dependentData.userDetails.imposterProfileID,
  ])

  const login = useCallback(() => {
    console.log('Logging Out - Inactive User')
    logout({ logoutParams: { returnTo: window.location.origin } })
  }, [logout])

  if (!impersonating) {
    return (
      <Dialog open={true}>
        {/* <Subtitles>Access Not Permitted</Subtitles> */}
        <Title>Inactive</Title>
        <UnauthorizedDialog>
          <DialogContentCenterbody>
            <InvertColorsOffIcon style={{ fontSize: '128px' }} />
          </DialogContentCenterbody>
          <BottomContent>
            This account is not active. To request access or account
            reactivation, please{' '}
            <a
              //   className={Styles.standardAnchor}
              href={`mailto: ${
                process.env.REACT_APP_CUSTOMER_SERVICE_EMAIL
              }?Subject=${encodeURI('Account Permissions Request')}`}
            >
              contact customer service.{' '}
            </a>
            To log in to another account, press the "Login" button.
          </BottomContent>
          <ButtonContainer>
            <Button variant="contained" onClick={login} fullWidth>
              Login
            </Button>
          </ButtonContainer>
        </UnauthorizedDialog>
      </Dialog>
    )
  }

  return (
    <Dialog open={true}>
      <Title>Inactive</Title>
      <UnauthorizedDialog>
        <DialogContentCenterbody>
          <InvertColorsOffIcon style={{ fontSize: '128px' }} />
        </DialogContentCenterbody>
        <BottomContent>
          This account is not active. To impersonate this user and view their
          account information, please reactivate their Datasight customer
          account. To do this without allowing user access, modify the user
          itself to revoke Datasight privileges or disable their user account
          specifically.
        </BottomContent>
        <DialogActions>
          <Button variant="contained" onClick={impersonate} fullWidth>
            Impersonate Another User
          </Button>
          <Button
            variant="contained"
            onClick={tryAgain}
            fullWidth
            disabled={
              !profileContext.dependentData.userDetails.imposterProfileID
            }
          >
            Try Again
          </Button>
          <Button variant="contained" onClick={login} fullWidth>
            Logout
          </Button>
        </DialogActions>
      </UnauthorizedDialog>
    </Dialog>
  )
}

export default InactiveUser
